import * as Yup from "yup"
import {
    CustomFieldsGroup,
    DeliveryTimesField,
    MeetingServiceConfig,
    DynamicConfigKey,
    DynamicConfigField,
    ExtendedDynamicConfigKey,
    BookingConfig,
    DeliveryFulfillmentType,
} from "./formTypes"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import {
    SelectedMainUserState,
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
} from "../../createOrderTypes"
import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import { createInitEvents } from "../resource/resourceBookingCalendars/helpers"
import { mapConfigFieldsToValues } from "./mapConfigFieldsToValues"
import {
    buildFieldSchema,
    getBookingResourceWithTableSetup,
    INVALID_DELIVERIES,
    isAccountingDimensionKey,
} from "./helpers"
import { buildMultipleDeliveriesState } from "./buildMultipleDeliveriesState"

export const defaultMultipleDeliveries = { deliveryDate: null, deliveryTimes: [], activeTime: null }

export const getAllConfigFields = (config?: MeetingServiceConfig | BookingConfig): DynamicConfigField[] => {
    if (!config?.sections?.length) return []

    return config.sections
        .filter((section) => section.showInDrawer !== false)
        .flatMap(({ groups }) => groups ?? [])
        .flatMap(({ fields }) => fields ?? [])
}

export function getValuesFromConfig(
    user: SelectedMainUserState,
    dateTimeFactory: DateTimeFactory,
    dialogType: CreateOrderDialogType,
    config?: MeetingServiceConfig | BookingConfig,
    orderDetails?: MeetingServiceOrder,
    mode?: CreateOrderDialogMode,
    flowConfig?: CreateOrderDialogFlow,
) {
    return mapConfigFieldsToValues({
        fields: getAllConfigFields(config),
        user,
        dialogType,
        orderDetails,
        dateTimeFactory,
        flowConfig,
        mode,
    })
}

export function getCustomFieldsValues(
    customFields: CustomFieldsGroup,
    dateTimeFactory: DateTimeFactory,
    dialogType: CreateOrderDialogType,
    orderDetails?: MeetingServiceOrder,
    mode?: CreateOrderDialogMode,
    flowConfig?: CreateOrderDialogFlow,
) {
    let result = { ...customFields }

    if (orderDetails) {
        const { webshop, merchant, priceList, bookingDetails, meetingDetails, deliveries } = orderDetails
        if (mode === "edit" && bookingDetails && bookingDetails.resources && bookingDetails.resources.length > 0) {
            const resourceIds = bookingDetails.resources.map((resource) => resource.id.toString())

            const start = dateTimeFactory.fromApi(meetingDetails.startDateTime).toISO()
            const end = dateTimeFactory.fromApi(meetingDetails.endDateTime).toISO()

            if (resourceIds.length > 0 && start && end) {
                result.event = createInitEvents(resourceIds, start, end)
            }

            result.fullResourcesList = bookingDetails.resources.map((resource) => ({
                title: resource.name,
                id: resource.id,
                type: {
                    id: resource.resourceTypeId,
                    name: resource.resourceType,
                },
                ...(resource.room?.tableSetup
                    ? {
                          tableSetups: [
                              {
                                  key: resource.room.tableSetup.key,
                                  name: resource.room.tableSetup.name,
                                  imageUrl: resource.room.tableSetup.imageUrl,
                              },
                          ],
                      }
                    : {}),
            }))
            result.resources = getBookingResourceWithTableSetup(bookingDetails.resources)
        }

        if (webshop && merchant && priceList) {
            const priceListValue = {
                merchantId: merchant.id,
                merchantName: merchant.name,
                priceListId: priceList.id,
                priceListName: priceList?.name || "",
                shopId: webshop.id,
                isOnlyOnePriceList: false,
            }
            result.priceList = priceListValue
            result.initPriceList = priceListValue
        }

        if (deliveries) {
            const md = buildMultipleDeliveriesState({
                mode,
                dialogType,
                flowConfig,
                orderDetails,
                dateTimeFactory,
            })
            if (md) {
                result[DynamicConfigKey.MULTIPLE_DELIVERIES] = md
            }
        }
        if (mode === "create" && flowConfig && orderDetails) {
            const start = dateTimeFactory.fromApi(meetingDetails.startDateTime)
            const end = dateTimeFactory.fromApi(meetingDetails.endDateTime)
            if (start && end) {
                result.event = createInitEvents([], start.toISO(), end.toISO())
            }
        }
    }

    return result
}

// Makes the field optional if PAYMENT_METHOD.value !== “INVOICE”
const relaxUnlessInvoice = (sch: Yup.Schema<any>) =>
    sch.when(`${DynamicConfigKey.PAYMENT_METHOD}.value`, (pmValue: unknown, s: Yup.Schema<any>) => {
        const tag = pmValue == null ? undefined : String(pmValue).toUpperCase()
        return tag === "INVOICE" ? s : s.notRequired().nullable()
    })

export function generateValidationSchema(
    dialogType: CreateOrderDialogType,
    meetingServiceConfiguration?: MeetingServiceConfig,
): Yup.ObjectSchema<Record<ExtendedDynamicConfigKey, any>> {
    const shape: Partial<Record<ExtendedDynamicConfigKey, Yup.Schema<any>>> = {}
    const isSpecialDialog = dialogType !== CreateOrderDialogType.ORDER

    const allFields: DynamicConfigField[] = meetingServiceConfiguration
        ? getAllConfigFields(meetingServiceConfiguration).filter(Boolean)
        : []

    const activeFieldKeys = new Set(allFields.filter(({ active }) => active).map(({ key }) => key))

    if (!isSpecialDialog) {
        allFields.forEach((config) => {
            const field = config.inputField
            const key = config.key
            const label = config.label || config.key

            if (
                key === DynamicConfigKey.DELIVERY_LOCATIONS ||
                key === DynamicConfigKey.OTHER_DELIVERY_LOCATION ||
                key === DynamicConfigKey.MULTIPLE_DELIVERIES ||
                key === DynamicConfigKey.SATELLITES
            )
                return

            if (!activeFieldKeys.has(key)) return
            shape[key] = buildFieldSchema(field, label)
        })

        if (activeFieldKeys.has(DynamicConfigKey.DELIVERY_LOCATIONS)) {
            shape[DynamicConfigKey.DELIVERY_LOCATIONS] = Yup.string().when(
                [DynamicConfigKey.DELIVERY_TYPE, DynamicConfigKey.OTHER_DELIVERY_LOCATION],
                ([type, otherLocation], schema) => {
                    const isDelivery = type === DeliveryFulfillmentType.DELIVERY
                    const isOtherEmpty = !otherLocation || otherLocation.trim() === ""

                    return isDelivery && isOtherEmpty
                        ? schema.required("Delivery location is required")
                        : schema.nullable()
                },
            )
        }

        if (activeFieldKeys.has(DynamicConfigKey.OTHER_DELIVERY_LOCATION)) {
            shape[DynamicConfigKey.OTHER_DELIVERY_LOCATION] = Yup.string().nullable()
        }
    }

    if (activeFieldKeys.has(DynamicConfigKey.MULTIPLE_DELIVERIES)) {
        shape[DynamicConfigKey.MULTIPLE_DELIVERIES] = Yup.mixed<DeliveryTimesField>()
            .test(
                "delivery-block-validation",
                "You must select a delivery date and at least one delivery time",
                (value) => {
                    if (!value) return false
                    const hasDate = !!value.deliveryDate
                    const hasAtLeastOneTime = Array.isArray(value.deliveryTimes) && value.deliveryTimes.length > 0
                    return hasDate && hasAtLeastOneTime
                },
            )
            .test("delivery-has-products", function (value) {
                if (!value || !Array.isArray(value.deliveryTimes)) return true

                const invalidTimes = value.deliveryTimes
                    .filter((delivery) => !delivery.orderLines || delivery.orderLines.length === 0)
                    .map((delivery) => delivery.time)

                if (invalidTimes.length === 0) {
                    return true
                }

                return this.createError({
                    message: `${INVALID_DELIVERIES}${invalidTimes.join(",")}`,
                })
            })
    }

    // if (activeFieldKeys.has(DynamicConfigKey.BUYER_PARTIES) && shape[DynamicConfigKey.BUYER_PARTIES]) {
    //     shape[DynamicConfigKey.BUYER_PARTIES] = relaxUnlessInvoice(shape[DynamicConfigKey.BUYER_PARTIES]!)
    // }

    if (!isSpecialDialog) {
        // 2) All accounting dimension keys → also NOT mandatory if ≠ INVOICE
        allFields
            .filter((f) => activeFieldKeys.has(f.key) && isAccountingDimensionKey(f.key))
            .forEach((f) => {
                const k = f.key as ExtendedDynamicConfigKey
                if (shape[k]) {
                    shape[k] = relaxUnlessInvoice(shape[k]!)
                }
            })
    }

    return Yup.object().shape(shape as Record<ExtendedDynamicConfigKey, Yup.Schema<any>>)
}
